import { useQueryBuilder } from '@/core/composables/useQueryBuilder';
import { UserCinema } from './user-cinema.interface';
import { notifyError } from '@/core/helpers/notification.helper';
import { Filters } from '@/core/store/filter.interface';
import { client } from '@/server';

export const baseUserCinemaQuery = `
SELECT
   -- Select main user_cinema fields
   uc.*,

   -- Construct the cinema JSON object directly in the query
json_object(
    'id', c.id,
    'title', c.title,
    'release_date', c.release_date,
    'overview', c.overview,
    'cover_url', c.cover_url,
    'backdrop_url', c.backdrop_url,
    'vote_average', c.vote_average,
    'vote_count', c.vote_count,
    'popularity', c.popularity,
    'adult', c.adult, -- json_object usually handles SQLite's 0/1 for boolean correctly
    'original_language', c.original_language,
    'original_title', c.original_title,
    -- If genre_ids is stored as a JSON array string (e.g., '[1, 2, 3]'),
    -- use json() to include it as a proper JSON array within the object.
    -- If stored differently (e.g., comma-separated), select raw c.genre_ids
    'genre_ids', c.genre_ids,
    'cinema_type', c.cinema_type
) AS cinema, -- Alias the resulting JSON object string as 'cinem
-- Construct the watch_status JSON object, handling potential NULLs from LEFT JOIN
CASE
   WHEN ws.id IS NULL THEN NULL -- Return SQL NULL if no watch status exists
   ELSE json_object(
      'id', ws.id,
      'name', ws.name,
      'key', ws.key,
      'rank', ws.rank
    )
END AS watch_status -- Alias the resulting JSON object string (or NULL) as 'watch_status'
   FROM
   user_cinema uc
INNER JOIN
   cinemas c ON uc.cinema_id = c.id
LEFT JOIN
   watch_status ws ON uc.watch_status_id = ws.id
`;

function userCinemaMapper(row: Record<string, any>) {
   return {
      ...row,
      cinema: JSON.parse(row.cinema as string),
      watch_status: row.watch_status ? JSON.parse(row.watch_status as string) : null,
      watched_season_episodes: row.watched_season_episodes ? JSON.parse(row.watched_season_episodes as string) : null,
   } as UserCinema;
}

export class UserCinemaService {
   async getAll(id: number, filters: Filters) {
      const query = `  
         ${baseUserCinemaQuery}
         WHERE uc.user_id = ?
         AND uc.partner_id IS NULL
      `;
      const { buildQuery } = useQueryBuilder(filters, query);
      try {
         const filterQuery = buildQuery();
         const response = await client.execute(filterQuery, [id]);
         return response.rows.map(userCinemaMapper);
      } catch (error) {
         console.error('Error fetching user cinemas:', error);
         notifyError({ title: 'Something went wrong while fetching the user cinemas' });
      }
   }

   async get(userCinemaId: number) {
      const query = `
         ${baseUserCinemaQuery}
         WHERE uc.id = ?
      `;
      try {
         const response = await client.execute(query, [userCinemaId]);
         if (response.rows.length === 0) return null;

         return userCinemaMapper(response.rows[0] as Record<string, any>);
      } catch (error) {
         console.error('Error fetching user cinema:', error);
         notifyError({ title: 'Something went wrong while fetching the user cinema' });
      }
   }

   async post(userCinema: Partial<UserCinema>) {
      const query = `
         INSERT INTO user_cinema (
            user_id,
            cinema_id,
            cinema_type,
            watch_status_id,
            watched_season_episodes
         ) VALUES (?, ?, ?, ?, ?)
         RETURNING id
      `;

      try {
         if (userCinema.user_id && userCinema.cinema_id && userCinema.cinema_type && userCinema.watch_status_id) {
            const insertResponse = await client.execute(query, [
               userCinema.user_id,
               userCinema.cinema_id,
               userCinema.cinema_type,
               userCinema.watch_status_id,
               JSON.stringify(userCinema.watched_season_episodes),
            ]);

            if (insertResponse.rows.length === 0 || !insertResponse.rows[0].id) return null;

            const response = await this.get(insertResponse.rows[0].id as number);

            return response;
         } else {
            console.error('Error: Missing required fields for user cinema creation');
            notifyError({ title: 'Missing required fields for user cinema creation' });
            return null;
         }
      } catch (error) {
         console.error('Error creating user cinema:', error);
         notifyError({ title: 'Something went wrong while creating the user cinema' });
      }
   }

   async put(id: number, watchStatusId: number, watchedSeasonEpisodes: string) {
      const query = `
         UPDATE user_cinema
         SET
            watch_status_id = ?,
            watched_season_episodes = ?
         WHERE id = ?
         RETURNING id
      `;

      try {
         const insertedRespons = await client.execute(query, [watchStatusId, watchedSeasonEpisodes, id]);

         if (insertedRespons.rows.length === 0 || !insertedRespons.rows[0].id) return null;

         const response = await this.get(insertedRespons.rows[0].id as number);
         return response;
      } catch (error) {
         console.error('Error updating user cinema:', error);
         notifyError({ title: 'Something went wrong while updating the user cinema' });
      }
   }

   async delete(id: number) {
      const query = `
         DELETE FROM user_cinema
         WHERE id = ?
      `;

      try {
         await client.execute(query, [id]);
      } catch (error) {
         console.error('Error deleting user cinema:', error);
         notifyError({ title: 'Something went wrong while deleting the user cinema' });
      }
   }

   async getByIds(userId: number, cinemaId: number, type: 'movie' | 'serie') {
      const query = `
         ${baseUserCinemaQuery}
         WHERE uc.user_id = ? AND uc.cinema_id = ? AND uc.partner_id IS NULL AND uc.cinema_type = ?
      `;
      try {
         const response = await client.execute(query, [userId, cinemaId, type]);
         if (response.rows.length === 0) return null;

         return userCinemaMapper(response.rows[0] as Record<string, any>);
      } catch (error) {
         console.error('Error fetching user cinema by IDs:', error);
         notifyError({ title: 'Something went wrong while fetching the user cinema by IDs' });
      }
   }
}
